import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button } from '@Comp';
import { FaChevronDown } from 'react-icons/fa';
import onboardingStore from '../../../stores/onboardingStore';
import OnboardingService from '../../../services/onboarding.service';
import { OauthIntegrations } from '../../../services/requests';
import './style.scss';

const ACCOUNTING_SYSTEMS = [
    { id: '1', name: 'Xero' }
];

@inject('commonStore', 'businessProfileStore')
@withRouter
@observer
export default class OnboardingStep5 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            linkMethod: 'select_system', // 'select_system' or 'upload_accounts'
            selectedSystem: '',
            fileName: '',
            apiError: null,
            xeroConnected: false,
            connectingXero: false,
            xeroError: null,
        };
    }

    async componentDidMount() {
        // Check for OAuth callback parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (code && state) {
            await this.handleXeroCallback(code, state);
        }
    }

    handleXeroCallback = async (code, state) => {
        const { commonStore, businessProfileStore } = this.props;

        try {
            this.setState({ connectingXero: true, xeroError: null });

            // Verify we're in onboarding context
            const context = localStorage.getItem('xero_oauth_context');
            if (context !== 'onboarding') {
                throw new Error('Invalid OAuth context');
            }

            // Verify state matches
            if (state !== businessProfileStore.randomString) {
                throw new Error('Invalid state parameter');
            }

            // Get the redirect URI used for OAuth initiation
            const redirectUri = localStorage.getItem('xero_redirect_uri');
            if (!redirectUri) {
                throw new Error('Missing redirect URI');
            }

            // Send code to backend with onboarding redirect URI
            const response = await OauthIntegrations.setAuthData({
                provider: 'xero',
                code,
                redirectUri // Pass the redirect URI to backend for token exchange
            });

            if (response.status === 200 || response.message === 'Xero account connected') {
                this.setState({
                    xeroConnected: true,
                    connectingXero: false,
                    selectedSystem: '1' // Set Xero as selected
                });
                commonStore.setMsg('Xero connected successfully!');

                // Clean up localStorage
                localStorage.removeItem('xero_oauth_context');
                localStorage.removeItem('xero_redirect_uri');

                // Clean URL
                window.history.replaceState({}, document.title, '/home/onboarding/step5');
            } else {
                throw new Error('Connection failed');
            }
        } catch (error) {
            console.error('Xero OAuth error:', error);
            // Clean up localStorage on error
            localStorage.removeItem('xero_oauth_context');
            localStorage.removeItem('xero_redirect_uri');
            this.setState({
                connectingXero: false,
                xeroError: 'Something went wrong. You can skip this step.'
            });
        }
    };

    getProviderAuthData = async () => {
        try {
            const response = await OauthIntegrations.getAuthData();
            return response.data || [];
        } catch (error) {
            console.error('Error fetching provider data:', error);
            return [];
        }
    };

    handleConnectXero = async () => {
        const { businessProfileStore } = this.props;

        try {
            this.setState({ connectingXero: true, xeroError: null });

            // Get Xero config from provider data
            const providerData = await this.getProviderAuthData();
            const xeroConfig = providerData.find(p => p.provider === 'xero');

            if (!xeroConfig) {
                this.setState({
                    xeroError: 'Something went wrong. You can skip this step.',
                    connectingXero: false
                });
                return;
            }

            const { clientId, scope, prefixUri } = xeroConfig;

            // Construct redirect URI for onboarding context
            const baseUrl = window.location.origin; // Gets http://localhost:3005 or https://app.nisafinance.com
            const redirectUri = `${baseUrl}/home/onboarding/step5`;

            const randomString = Math.random().toString(36).substring(3);
            businessProfileStore.randomString = randomString;

            // Store redirect context in localStorage for callback verification
            localStorage.setItem('xero_oauth_context', 'onboarding');
            localStorage.setItem('xero_redirect_uri', redirectUri);

            // Redirect to Xero OAuth with onboarding redirect URI
            window.location = `${prefixUri}&client_id=${clientId}&scope=${scope}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${randomString}`;
        } catch (error) {
            console.error('Error connecting to Xero:', error);
            this.setState({
                connectingXero: false,
                xeroError: 'Something went wrong. You can skip this step.'
            });
        }
    };

    handleMethodChange = (method) => {
        this.setState({ linkMethod: method });
    };

    handleSystemChange = (e) => {
        this.setState({ selectedSystem: e.target.value });
    };

    handleFileSelect = () => {
        // For now, just set a placeholder file name
        // In production, this would open a file picker
        this.setState({ fileName: 'management_accounts.pdf' });
    };

    handleBack = () => {
        this.props.history.push('/home/onboarding/step4');
    };

    handleSkip = async () => {
        const { commonStore } = this.props;

        try {
            // Clear previous errors
            this.setState({ apiError: null });
            commonStore.setLoading(true);

            // Gather onboarding data (without Step 5)
            const onboardingData = onboardingStore.getOnboardingData();

            const profileId = commonStore.businessProfile?.id;

            // Complete onboarding even though user skipped accounting system
            const result = await OnboardingService.completeOnboarding(onboardingData, profileId);

            if (result.success) {
                if (result.profileId && !profileId) {
                    await commonStore.getBusinessProfile(result.profileId);
                }

                onboardingStore.clearOnCompletion();

                commonStore.setMsg('Onboarding completed! You can add accounting system details later.');

                this.props.history.push('/home/onboarding/funding-information');
            }
        } catch (error) {
            console.error('Error completing onboarding:', error);

            // Set error state (non-blocking)
            this.setState({ apiError: 'Something went wrong. You can skip this step.' });
            commonStore.setMsg('Something went wrong. You can skip this step.');
        } finally {
            commonStore.setLoading(false);
        }
    };

    handleNext = async () => {
        const { linkMethod, selectedSystem, fileName } = this.state;
        const { commonStore } = this.props;

        // Validate form
        if (linkMethod === 'select_system' && !selectedSystem) {
            commonStore.setMsg('Please select an accounting system');
            return;
        }
        if (linkMethod === 'upload_accounts' && !fileName) {
            commonStore.setMsg('Please upload your management accounts');
            return;
        }

        try {
            // Clear previous errors
            this.setState({ apiError: null });
            commonStore.setLoading(true);

            // Save Step 5 data to store
            onboardingStore.setAccountingSystem(linkMethod, selectedSystem, fileName);
            onboardingStore.completeStep5();

            // Gather all onboarding data
            const onboardingData = onboardingStore.getOnboardingData();

            // Get existing profile ID if available
            const profileId = commonStore.businessProfile?.id;

            // Complete onboarding - save to backend
            const result = await OnboardingService.completeOnboarding(onboardingData, profileId);

            if (result.success) {
                // Update common store with profile ID
                if (result.profileId && !profileId) {
                    await commonStore.getBusinessProfile(result.profileId);
                }

                // Clear onboarding data from localStorage
                onboardingStore.clearOnCompletion();

                // Show success message
                commonStore.setMsg('Onboarding completed successfully! Your business profile has been pre-populated.');

                // Navigate to funding information
                this.props.history.push('/home/onboarding/funding-information');
            }
        } catch (error) {
            console.error('Error completing onboarding:', error);

            // Set error state (non-blocking)
            this.setState({ apiError: 'Something went wrong. You can skip this step.' });
            commonStore.setMsg('Something went wrong. You can skip this step.');
        } finally {
            commonStore.setLoading(false);
        }
    };

    isFinishEnabled = () => {
        const { linkMethod, selectedSystem, fileName, xeroConnected } = this.state;

        if (linkMethod === 'select_system') {
            // If Xero is selected, require connection
            if (selectedSystem === '1') {
                return xeroConnected;
            }
            return !!selectedSystem;
        }

        if (linkMethod === 'upload_accounts') {
            return !!fileName;
        }

        return false;
    };

    render() {
        const { linkMethod, selectedSystem, fileName, apiError, xeroConnected, connectingXero, xeroError } = this.state;

        return (
            <div className="step-content">
                {/* Loading Overlay for Xero OAuth Callback */}
                {connectingXero && (
                    <div className="xero-loading-overlay">
                        <div className="xero-loading-content">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="loading-text">Connecting to Xero...</p>
                            <p className="loading-subtext">Please wait while we complete your connection</p>
                        </div>
                    </div>
                )}

                <h3 className="step-title">Link your accounting system</h3>
                <p className="step-description">Upload your management accounts or connect your accounting system</p>

                {/* API Error Message (non-blocking) */}
                {apiError && (
                    <div className="alert alert-danger" role="alert" style={{ marginBottom: '20px' }}>
                        {apiError}
                    </div>
                )}

                {/* Link Method Options */}
                <div className="link-options">
                    {/* Select Accounting System Option */}
                    <div className="link-option">
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="linkMethod"
                                value="select_system"
                                checked={linkMethod === 'select_system'}
                                onChange={() => this.handleMethodChange('select_system')}
                            />
                            <span className="radio-text">Select your accounting system</span>
                        </label>

                        {linkMethod === 'select_system' && (
                            <div className="system-dropdown-container">
                                <div className="select-wrapper">
                                    <select
                                        className="system-select"
                                        value={selectedSystem}
                                        onChange={this.handleSystemChange}
                                    >
                                        <option value="">Select your accounting system</option>
                                        {ACCOUNTING_SYSTEMS.map(system => (
                                            <option key={system.id} value={system.id}>
                                                {system.name}
                                            </option>
                                        ))}
                                    </select>
                                    <FaChevronDown className="dropdown-icon" />
                                </div>

                                {/* Xero Connect Button */}
                                {selectedSystem === '1' && (
                                    <div className="xero-connect-container">
                                        {!xeroConnected ? (
                                            <>
                                                <Button
                                                    color="primary"
                                                    onClick={this.handleConnectXero}
                                                    disabled={connectingXero}
                                                    style={{ marginTop: '15px' }}
                                                >
                                                    {connectingXero ? 'Connecting...' : 'Connect to Xero'}
                                                </Button>
                                                {xeroError && (
                                                    <div className="error-message" style={{ marginTop: '10px', color: '#dc3545' }}>
                                                        {xeroError}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="success-message" style={{ color: '#28a745', marginTop: '15px', fontWeight: '500' }}>
                                                ✓ Xero connected successfully!
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Upload Management Accounts Option */}
                    <div className="link-option">
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="linkMethod"
                                value="upload_accounts"
                                checked={linkMethod === 'upload_accounts'}
                                onChange={() => this.handleMethodChange('upload_accounts')}
                            />
                            <span className="radio-text">Upload latest 3-month management-accounts</span>
                        </label>

                        {linkMethod === 'upload_accounts' && (
                            <div className="upload-container">
                                <Button
                                    color="secondary"
                                    outline
                                    size="sm"
                                    onClick={this.handleFileSelect}
                                >
                                    Choose File
                                </Button>
                                <span className="upload-text">
                                    {fileName || 'No file chosen'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleBack}
                    >
                        Back
                    </Button>
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleSkip}
                    >
                        Skip
                    </Button>
                    <Button
                        color="primary"
                        onClick={this.handleNext}
                        disabled={!this.isFinishEnabled()}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    }
}
